import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  Phone,
  ShieldCheck,
  Truck,
  Clock,
  ArrowRight,
  ArrowDown,
  Home,
  ArrowUpToLine,
  Package,
  Warehouse,
  Briefcase,
  MessageCircle,
  Star,
  Quote,
} from 'lucide-react';
import { KineticLines, FadeUp, Eyebrow } from '../components/motion';
import { EditorialMarquee } from '../components/EditorialMarquee';
import { CTASection } from '../components/CTASection';
import {
  IMAGES,
  PHONE_TEL,
  WHATSAPP_URL,
  SERVICES,
  STATS,
  CITIES,
  GALLERY,
  TESTIMONIALS,
} from '../data/content';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ICONS = { Home, Truck, ArrowUpToLine, Package, Warehouse, Briefcase };
const SPANS = ['tall', 'wide', 'wide', 'tall', 'wide', 'wide'];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[92vh] flex items-center overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Vira Nakliyat aracı yolda"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute bottom-0 left-0 right-0 h-40 hero-bottom-fade" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 w-full pt-32 pb-20">
        <FadeUp delay={0.1}>
          <Eyebrow>Vira Nakliyat — İnegöl / Bursa</Eyebrow>
        </FadeUp>

        <KineticLines
          className="mt-8 font-display font-medium uppercase tracking-tighter [word-spacing:0.14em] leading-[0.95] text-5xl sm:text-6xl lg:text-[5.5rem]"
          lines={[
            <span key="1" className="block">İnegöl&apos;den</span>,
            <span key="2" className="block text-[#FF5C00]">Tüm Türkiye&apos;ye</span>,
            <span key="3" className="block">Evden Eve Nakliyat</span>,
          ]}
        />

        <FadeUp delay={0.7} className="mt-8 max-w-xl">
          <p className="text-[#A0ABC0] text-base md:text-lg leading-relaxed">
            Türkiye&apos;nin 81 iline sigortalı, asansörlü ve profesyonel taşımacılık.
            Eşyalarınız bizimle güvende — keşif ücretsiz, fiyat net, teslimat zamanında.
          </p>
        </FadeUp>

        <FadeUp delay={0.9} className="mt-10 flex flex-wrap items-center gap-4">
          <a
            href={PHONE_TEL}
            data-testid="hero-call-button"
            className="group inline-flex items-center gap-3 bg-[#FF5C00] hover:bg-[#E65000] text-[#0A1128] font-bold rounded-full pl-6 pr-7 py-4 transition-colors duration-300"
          >
            <Phone size={18} strokeWidth={2.5} className="transition-transform duration-500 group-hover:rotate-12" />
            Hemen Teklif Al
          </a>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-button"
            className="inline-flex items-center gap-3 border border-[#F2F4F8]/25 hover:border-[#25D366] hover:text-[#25D366] text-[#F2F4F8] rounded-full px-6 py-4 transition-colors duration-300"
          >
            <MessageCircle size={18} />
            WhatsApp&apos;tan Yazın
          </a>
        </FadeUp>

        <FadeUp delay={1.1} className="mt-8">
          <Link
            to="/iletisim#teklif-formu"
            data-testid="hero-quote-form-link"
            className="group inline-flex items-center gap-2 text-sm text-[#A0ABC0] hover:text-[#FF5C00] transition-colors duration-300"
          >
            Aramak yerine online formu doldurun
            <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </Link>
        </FadeUp>

        <FadeUp delay={1.2} className="mt-10 flex flex-wrap gap-8">
          {[
            { icon: ShieldCheck, text: 'Sigortalı Taşıma' },
            { icon: Truck, text: 'Asansörlü Araç Filosu' },
            { icon: Clock, text: 'Zamanında Teslimat' },
          ].map((t, i) => (
            <div key={i} className="flex items-center gap-2.5 text-sm text-[#F2F4F8]/80">
              <t.icon size={16} className="text-[#FF5C00]" />
              {t.text}
            </div>
          ))}
        </FadeUp>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#A0ABC0]"
      >
        <ArrowDown size={18} className="animate-bounce" />
      </motion.div>
    </section>
  );
};

const ServicesPreview = () => (
  <section data-testid="services-preview" className="py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <FadeUp className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
        <div>
          <Eyebrow>Hizmetlerimiz</Eyebrow>
          <h2 className="mt-5 font-display font-medium tracking-tight text-4xl md:text-5xl leading-tight max-w-xl">
            Taşımanın her adımı <span className="text-[#FF5C00]">bizde</span>
          </h2>
        </div>
        <Link
          to="/hizmetler"
          data-testid="services-preview-all-link"
          className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[#A0ABC0] hover:text-[#FF5C00] transition-colors duration-300"
        >
          Tüm Hizmetler
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </FadeUp>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.icon];
          return (
            <FadeUp key={s.id} delay={i * 0.08}>
              <div
                data-testid={`service-card-${s.id}`}
                className="card-hover group h-full bg-[#0A1128] border border-[#1E293B] rounded-2xl p-8"
              >
                <div className="w-12 h-12 rounded-xl bg-[#FF5C00]/10 border border-[#FF5C00]/25 flex items-center justify-center mb-7 transition-colors duration-500 group-hover:bg-[#FF5C00]/20">
                  <Icon size={22} className="text-[#FF5C00]" />
                </div>
                <h3 className="font-display text-xl md:text-2xl tracking-tight mb-3">{s.title}</h3>
                <p className="text-[#A0ABC0] text-sm leading-relaxed">{s.desc}</p>
              </div>
            </FadeUp>
          );
        })}
      </div>
    </div>
  </section>
);

const StatsBand = () => (
  <section data-testid="stats-band" className="border-y border-[#1E293B] bg-[#0A1128]/60">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid grid-cols-2 lg:grid-cols-4 gap-10">
      {STATS.map((s, i) => (
        <FadeUp key={s.label} delay={i * 0.1} className="text-center lg:text-left">
          <div className="font-display font-semibold text-5xl md:text-6xl tracking-tighter text-[#F2F4F8]">
            {s.value}
          </div>
          <div className="mt-2 text-xs uppercase tracking-[0.25em] text-[#FF5C00]">{s.label}</div>
        </FadeUp>
      ))}
    </div>
  </section>
);

const Gallery = () => {
  const [photos, setPhotos] = useState(null);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/gallery`)
      .then(({ data }) => setPhotos(data))
      .catch(() => setPhotos([]));
  }, []);

  const items =
    photos && photos.length > 0
      ? photos.map((p, i) => ({
          src: `${BACKEND_URL}/api/gallery/file/${p.storage_path}`,
          title: p.title || 'Vira Nakliyat',
          tag: p.tag || 'Galeri',
          span: SPANS[i % SPANS.length],
        }))
      : GALLERY;

  return (
    <section data-testid="gallery-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <FadeUp className="mb-14">
          <Eyebrow>Galeri</Eyebrow>
          <h2 className="mt-5 font-display font-medium tracking-tight text-4xl md:text-5xl leading-tight max-w-2xl">
            Her taşıma, bir <span className="text-[#FF5C00]">özen</span> hikâyesi
          </h2>
        </FadeUp>

        <div className="grid grid-cols-2 md:grid-cols-4 grid-flow-dense auto-rows-[160px] md:auto-rows-[200px] gap-4">
          {items.map((g, i) => (
            <FadeUp
              key={i}
              delay={i * 0.07}
              className={
                g.span === 'tall'
                  ? 'row-span-2'
                  : g.span === 'wide'
                  ? 'col-span-2'
                  : ''
              }
            >
              <div
                data-testid={`gallery-item-${i}`}
                className="group relative w-full h-full rounded-2xl overflow-hidden border border-[#1E293B]"
              >
                <img
                  src={g.src}
                  alt={g.title}
                  loading="lazy"
                  className="img-zoom absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#060B19]/90 via-transparent to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-1 transition-transform duration-500 group-hover:translate-y-0">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#FF5C00]">{g.tag}</span>
                  <h3 className="font-display text-base md:text-lg tracking-tight mt-1">{g.title}</h3>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

const Stars = ({ rating }) => (
  <div className="flex gap-1">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        size={15}
        className={i < rating ? 'text-[#FF5C00] fill-[#FF5C00]' : 'text-[#1E293B]'}
      />
    ))}
  </div>
);

const Testimonials = () => (
  <section data-testid="testimonials-section" className="py-24 md:py-32 overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <FadeUp className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
        <div>
          <Eyebrow>Müşteri Yorumları</Eyebrow>
          <h2 className="mt-5 font-display font-medium tracking-tight text-4xl md:text-5xl leading-tight max-w-xl">
            Yeni yuvalarına bizimle <span className="text-[#FF5C00]">taşınanlar</span> anlatıyor
          </h2>
        </div>
        <div className="flex items-center gap-5 bg-[#0A1128] border border-[#1E293B] rounded-2xl px-7 py-5">
          <span className="font-display font-semibold text-5xl tracking-tighter">4.9</span>
          <div>
            <Stars rating={5} />
            <p className="text-xs text-[#A0ABC0] mt-1.5">Müşteri memnuniyeti</p>
          </div>
        </div>
      </FadeUp>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {TESTIMONIALS.map((t, i) => (
          <FadeUp key={i} delay={(i % 3) * 0.09}>
            <div
              data-testid={`testimonial-card-${i}`}
              className="card-hover relative h-full bg-[#0A1128] border border-[#1E293B] rounded-2xl p-8"
            >
              <Quote size={26} className="text-[#FF5C00]/30 absolute top-7 right-7" />
              <Stars rating={t.rating} />
              <p className="mt-5 text-[#F2F4F8]/90 text-sm leading-relaxed">&ldquo;{t.text}&rdquo;</p>
              <div className="mt-7 pt-6 border-t border-[#1E293B] flex items-center gap-4">
                <span className="w-10 h-10 rounded-full bg-[#FF5C00]/15 border border-[#FF5C00]/30 flex items-center justify-center font-display font-semibold text-[#FF5C00] text-sm">
                  {t.name.charAt(0)}
                </span>
                <div>
                  <p className="font-display text-sm tracking-tight">{t.name}</p>
                  <p className="text-xs text-[#A0ABC0] mt-0.5">{t.route}</p>
                </div>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Coverage = () => (
  <section data-testid="coverage-section" className="py-24 md:py-32 bg-[#0A1128] border-y border-[#1E293B] overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
      <FadeUp>
        <Eyebrow>Hizmet Bölgeleri</Eyebrow>
        <h2 className="mt-5 font-display font-medium tracking-tight text-4xl md:text-5xl leading-tight">
          İnegöl merkezli,
          <br />
          <span className="text-[#FF5C00]">81 ile</span> kesintisiz hat
        </h2>
        <p className="mt-6 text-[#A0ABC0] leading-relaxed max-w-md">
          İnegöl&apos;den Türkiye&apos;nin her iline, tüm illerden İnegöl&apos;e. Popüler
          güzergâhlarımızın yanı sıra talebinize özel her rotayı planlıyoruz.
        </p>
        <a
          href={PHONE_TEL}
          data-testid="coverage-call-button"
          className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[#FF5C00] group"
        >
          Rotanızı Sorun
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      </FadeUp>

      <FadeUp delay={0.15}>
        <div className="flex flex-wrap gap-3">
          {CITIES.map((c, i) => (
            <span
              key={c}
              data-testid={`city-chip-${i}`}
              className="px-5 py-2.5 rounded-full border border-[#1E293B] bg-[#121A2F] text-sm text-[#F2F4F8]/85 hover:border-[#FF5C00] hover:text-[#FF5C00] transition-colors duration-300 cursor-default"
            >
              İnegöl ⇄ {c}
            </span>
          ))}
          <span className="px-5 py-2.5 rounded-full bg-[#FF5C00] text-sm font-bold text-[#0A1128]">
            +63 İl Daha
          </span>
        </div>
      </FadeUp>
    </div>
  </section>
);

export default function HomePage() {
  return (
    <main data-testid="home-page">
      <Hero />
      <EditorialMarquee />
      <ServicesPreview />
      <StatsBand />
      <Gallery />
      <Testimonials />
      <Coverage />
      <CTASection />
    </main>
  );
}
